import { type FC, useCallback, useEffect } from 'react';

import type { TreeNode } from 'tree-model';

import type { AssetsHttpGetCollectionsQuery } from '@iconik-io/iconik-api/src/services/assets';
import type { SearchItemEntity } from '@iconik-io/shared-lib/src/redux/entities/normalized/search_item';

import { searchCollectionTreeDebounced } from '../../components/modals/add_to_collection_advanced/thunks';
import permissionsManager, {
  type PermissionManagerInjectedProps,
} from '../../components/permissions_manager/permissions_manager';
import { NEW_COLLECTION_MODAL } from '../../constants/modals';
import { ID_COLLECTION } from '../../constants/reducer_ids';
import {
  COLLECTION_PAGE_ROUTE,
  SHARE_COLLECTION_PAGE_ROUTE,
} from '../../constants/routes';
import { segmentTrackEvent } from '../../ducks/analytics_tracking/thunks';
import {
  getCollectionContents,
  getCollections,
} from '../../ducks/collection_fetch/thunks';
import {
  createAddCollection,
  moveCopyItemsToCollection,
} from '../../ducks/collections/thunks';
import { SEARCH_DRAG } from '../../ducks/dragged_items/constants';
import { historyPush } from '../../ducks/history/actions';
import { errorMessage } from '../../ducks/messages/thunks';
import { openModal } from '../../ducks/modals_manager/slice';
import { getSearchItems } from '../../ducks/pages/search/thunks';
import { searchFilterActions } from '../../ducks/panels/search_filters/actions';
import { selectIsSharePage } from '../../ducks/share/selectors';
import { getShareSubcollections } from '../../ducks/share/thunks';
import { clearTree, modifyBranchByNode } from '../../ducks/tree/thunks';
import type { TreeReducerModel } from '../../ducks/tree/types';
import { useLeafRouteParams } from '../../router/use_leaf_route_params';
import { useAppDispatch, useAppSelector } from '../../store/react_redux_typed';
import {
  getParentCollectionsFromTree,
  isCollectionMappedToStorage,
} from '../../utils/collections';
import { getObjectLink } from '../../utils/get_object_link';
import { selectUserRoles } from '../../utils/get_user_roles';
import { UUID_ZERO } from '../../utils/tree_handlers';
import {
  CollectionsPanelComponent,
  type NewCollectionFormData,
} from './component';
import {
  selectAllDropTargetIds,
  selectCollectionsTreeItems,
  selectSharedCollectionsTreeItems,
} from './selectors';
import { useRevealCollection } from './use_reveal_collection';

type Props = PermissionManagerInjectedProps;

const CollectionsPanel: FC<Props> = (props) => {
  const collectionsTree = useAppSelector((state) => state.collectionsTree);
  const allDropTargetIds = useAppSelector((state) =>
    selectAllDropTargetIds(state, SEARCH_DRAG),
  );
  // Route, params and query all come from the same match: see PanelsContainer.
  const { route, params, query } = useLeafRouteParams();
  const routeId = params.id;
  const collection = useAppSelector((state) =>
    routeId ? state.entities.collections[routeId] : undefined,
  );
  const createCollectionWithParentCollectionACLs = useAppSelector(
    (state) => state.settings.merged.collections_get_parent_acls ?? false,
  );
  const isShare = useAppSelector(selectIsSharePage);
  const items = useAppSelector((state) =>
    !isShare
      ? selectCollectionsTreeItems(state)
      : selectSharedCollectionsTreeItems(state),
  );
  const lockMappedCollections = useAppSelector(
    (state) => state.settings.merged.lock_mapped_collections,
  );
  const roles = useAppSelector(selectUserRoles);
  const shareObjectId = useAppSelector((state) =>
    isShare ? state.share.share.object_id : undefined,
  );
  const treeSearchQuery = useAppSelector(
    (state) => state.collectionsTree.treeSearchQuery,
  );

  const dispatch = useAppDispatch();

  const fetchCollections = useCallback(
    (queryParams: AssetsHttpGetCollectionsQuery = {}) => {
      if (isShare) {
        return query.object_id
          ? dispatch(getShareSubcollections(query.object_id))
          : undefined;
      }
      return dispatch(getCollections(queryParams));
    },
    [isShare, query.object_id],
  );

  const collectionId = [
    COLLECTION_PAGE_ROUTE,
    SHARE_COLLECTION_PAGE_ROUTE,
  ].includes(route)
    ? routeId
    : undefined;

  useEffect(() => {
    if (roles.canReadCollections && !collectionsTree.flatTree.length) {
      fetchCollections();
    }
  }, [fetchCollections, roles.canReadCollections, collectionsTree.flatTree]);

  useRevealCollection(collectionId, roles.canReadCollections);

  const onResultItemsDrop = (
    searchItems: SearchItemEntity[],
    id: string,
    copyMode: boolean,
  ) => {
    const parent = collectionsTree.flatTree.find(
      (item) => item.model.id === id,
    );
    if (!parent) {
      return;
    }

    addToCollection(parent, searchItems, copyMode);
  };

  const addToCollection = (
    parent: TreeNode<TreeReducerModel>,
    searchItems: SearchItemEntity[],
    copyMode: boolean,
  ) => {
    const { id, parents, permissions } = parent.model;
    dispatch(
      moveCopyItemsToCollection(
        id,
        parents,
        permissions,
        searchItems,
        copyMode,
      ),
    );
  };

  const onExpand = (node: TreeNode<TreeReducerModel>) => {
    if (!node.model.expanded && !node.children.length) {
      dispatch(getCollectionContents(node.model.id));
    }
    dispatch(
      modifyBranchByNode(ID_COLLECTION, node, {
        expanded: !node.model.expanded,
      }),
    );

    // if the root element gets collapsed we refresh the tree
    if (node.model.id === UUID_ZERO && node.model.expanded) {
      dispatch(clearTree(ID_COLLECTION));
      fetchCollections();
    }
  };

  const onLoadMore = (node: TreeNode<TreeReducerModel>) => {
    const page = Number(node.model.page) + 1;
    if (treeSearchQuery) {
      dispatch(
        searchCollectionTreeDebounced(treeSearchQuery, ID_COLLECTION, page),
      );
    } else if (node.model.id === UUID_ZERO) {
      fetchCollections({
        page,
      });
    } else {
      dispatch(
        getCollectionContents(node.model.id, {
          page,
        }),
      );
    }
  };

  const handleScrollEnds = useCallback(() => {
    if (treeSearchQuery) {
      return;
    }
    const { model } = collectionsTree.treeModel.treeRoot;
    const page = (model.page ?? 0) + 1;
    const isLastPage = page > (model.pages ?? 0);
    const loading = model.loading;
    if (!loading && !isLastPage) {
      fetchCollections({ page });
    }
  }, [fetchCollections, treeSearchQuery, collectionsTree.treeModel.treeRoot]);

  const onClick = (node: TreeNode<TreeReducerModel>) => {
    const { id } = node.model;

    dispatch(searchFilterActions.setQuery(''));

    const paramId = routeId;
    if (id === paramId) {
      dispatch(getSearchItems());
    }
    const targetId = !id || id === UUID_ZERO ? shareObjectId : id;
    if (!targetId) {
      dispatch(historyPush('/search'));
      return;
    }
    const objectLink = getObjectLink(isShare, targetId, 'collections');
    dispatch(historyPush(objectLink));
  };

  const validCollection = async (
    data: NewCollectionFormData,
    reset: (model?: NewCollectionFormData) => void,
  ) => {
    if (data.title) {
      reset();
      const targetCollection: TreeNode<TreeReducerModel> | undefined =
        collectionsTree.flatTree.find((item) => item.model.id === collectionId);

      const isTargetCollectionMapped = getParentCollectionsFromTree(
        targetCollection,
      ).some(isCollectionMappedToStorage);

      if (isTargetCollectionMapped) {
        if (lockMappedCollections) {
          dispatch(
            errorMessage({
              text: 'Cannot edit mapped collections',
            }),
          );
          return;
        }
        dispatch(
          openModal({
            id: NEW_COLLECTION_MODAL,
            contextItems: targetCollection?.model
              ? [targetCollection?.model]
              : [],
            options: { title: data.title },
          }),
        );
        return;
      }

      if (collectionId) {
        const aclSettings = createCollectionWithParentCollectionACLs
          ? { apply_collection_acls: true }
          : {
              apply_default_acls: true,
              restrict_collection_acls: true,
            };
        await dispatch(createAddCollection(data, collectionId, aclSettings));
      } else {
        await dispatch(createAddCollection(data));
      }

      dispatch(
        segmentTrackEvent({
          eventName: 'Collection Created',
          properties: {
            IsSubcollection: Boolean(collectionId),
          },
        }),
      );
    }
  };

  const loading = collectionsTree.treeModel.treeRoot.model.loading ?? false;

  return (
    <CollectionsPanelComponent
      write={roles.canWriteCollections}
      readOnly={collectionId ? !props.permissionsRolesMerge?.write : false}
      collectionId={collectionId}
      collection={collection}
      validCollection={validCollection}
      tree={collectionsTree.flatTree}
      loading={loading}
      items={items}
      onExpand={onExpand}
      onScrollEnds={handleScrollEnds}
      onLoadMore={onLoadMore}
      lockMappedCollections={lockMappedCollections}
      errorMessage={(message) => dispatch(errorMessage(message))}
      onResultItemsDrop={onResultItemsDrop}
      allDropTargetIds={allDropTargetIds}
      onClick={onClick}
      selectedCollectionId={collectionId}
      roles={roles}
      route={route}
      openModal={(modal) => dispatch(openModal(modal))}
    />
  );
};

export default permissionsManager(CollectionsPanel);
